import { regulationLabel } from '@/utils/regulationLabels'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { StatePanel } from '@/components/common'
import { LegList, LegSearch } from '@/components/leg-search'
import { useOrganization } from '@/contexts/OrganizationContext'
import { aiApi, prepacksApi } from '@/services/api'
import { PrepackCard } from './PrepackCard'
import { defaultTemplateId, deriveBetType, betTypeLabel, isPlayerLeg, legBetLabel, packRecipeId, previewLegTeam, publishMinLegs, templateJerseyNumbersNeedResave, templateShowsJerseyNumbers, templateShowsJerseys } from './prepackCardModel'
import { overriddenFields } from './prepackOverridesModel'
import { UNSCHEDULED_PACKS_PATH, preloadScheduleBuilder, schedulePacksPath, schedulePath } from './prepackSchedulePaths'
import { scheduleQueryKeys, useScheduleSources } from './prepackScheduleQueries'
import { normalizeTemplates } from '../../../../../shared/pkg/prepacks/templateVariants.js'
import { BANNER_MODE_STATIC, BANNER_MODE_VAR } from '../../../../../shared/pkg/prepacks/templateBuilder.js'
import { TAG_STYLE_FILLED, TAG_STYLE_QUIET, TAG_STYLE_VARIABLE } from '../../../../../shared/pkg/prepacks/templateRendering.js'
import { PrepackSurfacePreviewProvider } from './PrepackPreviewProvider'
import { PrepackSurfaceLiveTag } from './PrepackSurfaceLiveTag'
import { LiveAppearanceControls, LiveCopyFields, LivePrepackCard, LiveTemplatePicker } from './PrepackLiveCopy'
import { createPrepackCopyStore, resolvePackCopy } from './prepackCopyStore'
import { copySuggestionLegKey } from './usePrepackCopySuggestions'
import PrepackLivePreview from './PrepackLivePreview'
import PrepackQuoteStatus from './PrepackQuoteStatus'
import { targetHasCanonicalPack, usePrepackQuoteTargets } from './usePrepackQuoteTargets'
import PrepackRegulationPicker from './PrepackRegulationPicker'
import { defaultTargetRegulations } from './prepackRegulationTargets'
import PrepackTagsField from './PrepackTagsField'
import PrepackSuggestedLegs from './PrepackSuggestedLegs'
import { repeatWarningLines, surfaceNamer } from './prepackRepeatsModel'

function surfaceDimension(surface) {
  if (surface?.layout_config?.dimension) return surface.layout_config.dimension
  if (surface?.slug?.includes('sport')) return 'sport'
  if (surface?.slug?.includes('league')) return 'league'
  if (surface?.slug?.includes('event') || surface?.slug?.includes('live')) return 'event'
  return null
}

function surfaceLabel(surface) {
  return surface.name || surface.slug
}


// What the operator chooses per leg in the Jerseys box (#572): the player's
// shirt number and the alternate kit. The server copies both onto the leg's
// team. Mirrors Go's models.PrepackLegChoices: a new choice goes in both.
const LEG_CHOICES = ['player_number', 'kit_variant']

// The most legs any pack has (the server's MaxPackLegs).
const MAX_PACK_LEGS = 6

// The leg as the quote and copy suggestions read it. The jersey choices are
// left out so typing a shirt number neither re-prices the pack nor re-suggests
// its title: save and the preview put them back on the quoted legs
// (withLegChoices).
function canonicalLeg(leg) {
  const { player: _player, bet: _bet, game: _game, ...clean } = leg
  for (const key of LEG_CHOICES) delete clean[key]
  return clean
}

// The leg of `candidates` that stands for `leg`: a target's legs follow the
// selection's order (they can carry another regulation's outcome ids), so the
// match is by position when the counts agree, else by outcome id.
function counterpartLeg(candidates, leg, index, total) {
  if (candidates.length === total) return candidates[index]
  return candidates.find((item) => String(item.outcome_id) === String(leg.outcome_id))
}

// The quoted legs of a target with the jersey choices made on the selected
// legs (matched by counterpartLeg). Empty choices are left off.
function withLegChoices(legs, selectedLegs) {
  return legs.map((leg, index) => {
    const source = counterpartLeg(selectedLegs, leg, index, legs.length)
    const next = { ...leg }
    for (const key of LEG_CHOICES) {
      if (source?.[key]) next[key] = source[key]
      else delete next[key]
    }
    return next
  })
}

// The preview draws the choices the way the server will store them: on the
// leg's team, which the jersey reads. Only the preview; save never sends a team.
function withPreviewTeamChoices(legs) {
  return legs.map((leg) => (leg.team ? { ...leg, team: previewLegTeam(leg) } : leg))
}


function targetDraftId() {
  return crypto.randomUUID()
}

function errorMessage(error) {
  return error?.response?.data?.error || error?.message || 'The target could not be saved.'
}

function targetFingerprint(payload) {
  return JSON.stringify(payload)
}

const VOLATILE_TARGET_LEG_FIELDS = new Set([
  'odds',
  'decimal_odds',
  'american_odds',
  'price',
  'price_display',
  'quoted_at',
  'valid_until',
  'quote_id',
  'updated_at',
])

function targetWagerFingerprint(regulation, pack) {
  const legs = (pack?.legs || []).map((leg) => Object.fromEntries(
    Object.entries(leg || {}).filter(([key]) => !VOLATILE_TARGET_LEG_FIELDS.has(key)),
  ))
  return targetFingerprint({ regulation, legs })
}

function targetContentFingerprint(regulation, payload) {
  return targetFingerprint({
    regulation,
    title: payload.title,
    title_source: payload.title_source,
    subtitle: payload.subtitle,
    subtitle_source: payload.subtitle_source,
    template_id: payload.template_id,
    template_vars: payload.template_vars,
    tags: payload.tags,
    surfaces: payload.surfaces,
    leg_choices: (payload.legs || []).map((leg) => LEG_CHOICES.map((key) => leg[key] || '')),
  })
}

function versionToken(value) {
  if (value == null) return null
  if (typeof value === 'object') return value.updated_at ?? value.version ?? null
  return value
}



export default function PrepacksGeneratePage() {
  const [searchParams] = useSearchParams()
  const { currentOrg } = useOrganization()
  const [revision, setRevision] = useState(0)
  const initialSurface = searchParams.get('surface')
  const editId = searchParams.get('id')

  return (
    <SinglePackBuilder
      key={`${currentOrg?.slug}-${editId || 'new'}-${revision}`}
      initialSurface={initialSurface}
      editId={editId}
      onReset={() => setRevision((value) => value + 1)}
    />
  )
}

const LEAVE_EDIT_MESSAGE = 'This prepack has unsaved changes. Leave without saving?'

// Quiet is the card's default, so a quiet pack stores no key (prepacks.ValidateTagStyle).
function withTagStyle(vars, style) {
  const next = { ...(vars || {}) }
  if (style === TAG_STYLE_FILLED) next[TAG_STYLE_VARIABLE] = style
  else delete next[TAG_STYLE_VARIABLE]
  return next
}

// What a template switch keeps of the pack's own values: its picture mode (an edited pack that was settled stays
// settled, so the banner worker never paints it) and its tag style.
const TEMPLATE_SWITCH_KEPT_VARS = [BANNER_MODE_VAR, TAG_STYLE_VARIABLE]

function templateSwitchVars(vars) {
  const kept = {}
  for (const key of TEMPLATE_SWITCH_KEPT_VARS) {
    if (vars?.[key]) kept[key] = vars[key]
  }
  return kept
}

// What an edit of a saved pack can change, as one comparable string: the
// builder's state after hydration against the pack it hydrated from. Legs
// carry the jersey choices save keeps (withLegChoices: an empty one is left
// off); `surfaces` is the pack's own list as hydrated, then the operator's pick.
const editSnapshot = ({ title, subtitle, tags, templateId, templateVars, legs, regulations, surfaces }) => JSON.stringify({
  title: title || '',
  subtitle: subtitle || '',
  tags: Array.isArray(tags) ? tags : [],
  templateId: templateId ?? null,
  templateVars: templateVars || {},
  legs: (legs || []).map((leg) => [leg.outcome_id, ...LEG_CHOICES.map((key) => leg[key] || null)]),
  regulations,
  surfaces: surfaces ?? null,
})
const savedPackSnapshot = (pack) => editSnapshot({
  title: pack.title,
  subtitle: pack.subtitle,
  tags: pack.tags,
  templateId: pack.template_id,
  templateVars: pack.template_vars,
  legs: pack.legs,
  regulations: pack.regulation ? [pack.regulation] : [],
  surfaces: pack.surfaces || [],
})

function SinglePackBuilder({ initialSurface, editId, onReset }) {
  const navigate = useNavigate()
  const { currentOrg, hasProductAccess } = useOrganization()
  const slug = currentOrg?.slug
  const queryClient = useQueryClient()
  const canConfigure = hasProductAccess('prepacks', 'configure')
  const canPublish = hasProductAccess('prepacks', 'publish')

  // Title and tagline: read at save time, shown by the Live* components.
  const [copyStore] = useState(createPrepackCopyStore)
  const [templateId, setTemplateId] = useState(undefined)
  const [templateVars, setTemplateVars] = useState({})
  // A banner picture painting in the appearance panel (#606): saving waits for it, so the pack keeps the picture.
  const [bannerPainting, setBannerPainting] = useState(false)
  const paintingNoteId = useId()
  const [tags, setTags] = useState([])
  const [legSearch, setLegSearch] = useState('')
  const [sourceRegulation, setSourceRegulation] = useState('')
  const [targetRegulations, setTargetRegulations] = useState([])
  const [previewRegulation, setPreviewRegulation] = useState('')
  const [selectedLegs, setSelectedLegs] = useState([])
  // A saved pack's own legs never trigger automatic suggestions.
  const [loadedLegKey, setLoadedLegKey] = useState(null)
  const [surfaceOverride, setSurfaceOverride] = useState(null)
  const [hydratedId, setHydratedId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)
  const [targetSaveStates, setTargetSaveStates] = useState({})

  // Stable IDs, versions and last reviewed payload fingerprints per target.
  const targetDraftsRef = useRef({})
  const targetDraftScopeRef = useRef(null)
  const targetInitializationRef = useRef(null)

  const mountedRef = useRef(true)
  const selectedLegsHeadingRef = useRef(null)
  const saveGenerationRef = useRef(0)
  const saveScopeRef = useRef('')

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      saveGenerationRef.current += 1
    }
  }, [])

  const regulationQuery = useQuery({
    queryKey: ['prepacks', 'regulations', slug],
    queryFn: () => prepacksApi.getRegulations(slug),
    enabled: Boolean(slug),
  })
  const surfacesQuery = useQuery({
    queryKey: ['prepacks', 'surfaces', slug],
    queryFn: () => prepacksApi.listSurfaces(slug),
    enabled: Boolean(slug),
  })
  const configQuery = useQuery({
    queryKey: ['prepacks', 'configuration', slug],
    queryFn: () => prepacksApi.getConfiguration(slug),
    enabled: Boolean(slug),
  })
  // Automatic suggestions need a confirmed capability; manual ones do not.
  const aiCapabilitiesQuery = useQuery({
    queryKey: ['ai-capabilities', slug],
    queryFn: () => aiApi.getCapabilities(slug),
    enabled: Boolean(slug && canConfigure),
    staleTime: 60000,
    retry: false,
  })
  const prepackTitleFeature = aiCapabilitiesQuery.data?.features?.prepack_titles
  const titleAiStatus = prepackTitleFeature?.enabled === true
    ? 'enabled'
    : (prepackTitleFeature?.enabled === false ? 'disabled' : 'unknown')
  const editQuery = useQuery({
    queryKey: ['prepacks', 'library', slug, editId],
    queryFn: () => prepacksApi.getPrepack(slug, editId),
    enabled: Boolean(slug && editId),
  })
  // The schedule that generated the pack being edited, named under the heading,
  // and the one a saved draft carries, which lists it under Packs (#1080).
  const editRecipeId = editId ? packRecipeId(editQuery.data) : null
  const resultRecipeId = result ? packRecipeId(result.pack) : null
  const sourceIds = [editRecipeId, resultRecipeId].filter(Boolean)
  const scheduleSourceOf = useScheduleSources(slug, { enabled: sourceIds.length > 0, ids: sourceIds })
  const editSource = editId && editQuery.data ? scheduleSourceOf(editQuery.data) : null
  // The fields an earlier edit made this pack's own (#1089).
  const editedFields = editId && editQuery.data ? overriddenFields(editQuery.data) : []
  // Read when the schedule link is clicked: the title and tagline live in the
  // copy store, which the builder itself does not subscribe to.
  const hasUnsavedEdits = () => {
    const pack = editQuery.data
    if (!editId || !pack || hydratedId !== pack.id) return false
    const { title, subtitle } = copyStore.get()
    return editSnapshot({ title, subtitle, tags, templateId, templateVars, legs: selectedLegs, regulations: targetRegulations, surfaces: surfaceOverride }) !== savedPackSnapshot(pack)
  }
  const leaveForSchedule = (event) => {
    if (hasUnsavedEdits() && !window.confirm(LEAVE_EDIT_MESSAGE)) event.preventDefault()
  }

  const availableRegulations = useMemo(() => regulationQuery.data?.regulations || [], [regulationQuery.data?.regulations])
  const effectiveSourceRegulation = sourceRegulation || editQuery.data?.regulation || regulationQuery.data?.regulation || ''
  const canSearchOutcomes = Boolean(slug && effectiveSourceRegulation && (!editId || hydratedId === editId))
  const maxLegs = MAX_PACK_LEGS

  const surfaces = useMemo(() => surfacesQuery.data?.surfaces || [], [surfacesQuery.data?.surfaces])
  // Keep the fallback in card rendering inputs; the picker filters it from
  // artwork choices but PrepackCard needs it for native/default styling.
  const templates = useMemo(() => normalizeTemplates(configQuery.data?.card_templates), [configQuery.data?.card_templates])

  // Hydrate edit pack
  useEffect(() => {
    if (!editQuery.data || hydratedId === editQuery.data.id) return
    const pack = editQuery.data
    const targetReg = pack.regulation || ''
    setHydratedId(pack.id)
    copyStore.set({
      title: pack.title || '',
      titleSource: pack.title_source || 'operator',
      subtitle: pack.subtitle || '',
      subtitleSource: pack.subtitle_source || 'operator',
    })
    setTemplateId(pack.template_id ?? null)
    setTemplateVars(pack.template_vars || {})
    setTags(Array.isArray(pack.tags) ? pack.tags : [])
    setSourceRegulation(targetReg)
    setTargetRegulations(targetReg ? [targetReg] : [])
    setPreviewRegulation(targetReg)
    targetDraftScopeRef.current = `${editId || 'new'}:${targetReg}`
    targetDraftsRef.current = targetReg
      ? {
          [targetReg]: {
            id: pack.id,
            version: pack.updated_at ?? pack.version ?? null,
            status: pack.status,
            persisted: true,
            recipe_id: pack.recipe_id,
            recipe_revision: pack.recipe_revision,
            pack_type: pack.pack_type,
          },
        }
      : {}
    // Legs as stored: the legacy `player`/`bet`/`game` aliases the composer once
    // added here are explicit card-slot overrides to the renderer (sample packs),
    // and on a real pack they hid the participant and the bet line (#865).
    const loadedLegs = pack.legs || []
    setSelectedLegs(loadedLegs)
    setLoadedLegKey(copySuggestionLegKey(loadedLegs.map(canonicalLeg)))
    setSurfaceOverride(pack.surfaces || [])
  }, [copyStore, editId, editQuery.data, hydratedId])

  // Default template initialization
  useEffect(() => {
    if (!editId && templateId === undefined && configQuery.data) {
      setTemplateId(defaultTemplateId(configQuery.data) ?? null)
    }
  }, [editId, templateId, configQuery.data])
  // A new pack takes the brand's default template, known once the
  // configuration loads. Until then there is no template to choose from or to
  // restyle: the picker would show the Default card and then switch to the
  // default, losing a choice made in between.
  const templatePending = !editId && templateId === undefined && !configQuery.isError

  // The targets a search regulation starts with: a new pack targets every
  // assigned regulation (#810); an edit is one regulation's pack, so it never
  // takes the others (they would be saved as new packs).
  const initialTargetsFor = useCallback((source) => (
    editId ? [source] : defaultTargetRegulations(availableRegulations, source)
  ), [availableRegulations, editId])

  // Initialize targets once for each source scope. An edit's targets come from
  // the pack itself; an explicit Clear all must remain empty.
  useEffect(() => {
    if (!effectiveSourceRegulation || availableRegulations.length === 0) return
    const scope = `${editId || 'new'}:${effectiveSourceRegulation}`
    if (targetInitializationRef.current === scope) return
    targetInitializationRef.current = scope
    if (!editId && selectedLegs.length === 0) {
      setTargetRegulations(initialTargetsFor(effectiveSourceRegulation))
      setPreviewRegulation(effectiveSourceRegulation)
    }
  }, [availableRegulations.length, editId, effectiveSourceRegulation, initialTargetsFor, selectedLegs.length])

  // Ensure previewRegulation is in targetRegulations
  useEffect(() => {
    if (targetRegulations.length > 0 && !targetRegulations.includes(previewRegulation)) {
      setPreviewRegulation(targetRegulations[0])
    }
  }, [targetRegulations, previewRegulation])

  // Allocate operation IDs before any target save request. A source-scope change
  // starts a new operation and must not reuse another source's draft identity.
  useEffect(() => {
    if (!effectiveSourceRegulation) return
    const scope = `${editId || 'new'}:${effectiveSourceRegulation}`
    if (targetDraftScopeRef.current !== scope) {
      targetDraftScopeRef.current = scope
      targetDraftsRef.current = {}
      setTargetSaveStates({})
    }
    for (const regulation of targetRegulations) {
      if (!targetDraftsRef.current[regulation]) {
        targetDraftsRef.current[regulation] = { id: targetDraftId(), persisted: false, newPack: true }
      }
    }
  }, [editId, effectiveSourceRegulation, targetRegulations])

  const requestedSurface = surfaces.find(
    (surface) => surface.slug === initialSurface || surface.slug === `prepacks-${initialSurface}` || surface.name.toLowerCase() === String(initialSurface || '').toLowerCase()
  )
  const inferredSurfaces = useMemo(() => {
    if (surfaces.length === 0) return []
    const inferred = surfaces
      .filter((surface) => {
        if (!surface.is_enabled || surface.prepacks_enabled === true) return false
        const dimension = surfaceDimension(surface)
        if (!dimension) return true
        if (selectedLegs.length === 0) return false
        return selectedLegs.some((leg) => (dimension === 'event' ? Boolean(leg.event_label) : Boolean(leg[dimension])))
      })
      .map((surface) => surface.slug)
    if (requestedSurface && !inferred.includes(requestedSurface.slug)) inferred.unshift(requestedSurface.slug)
    if (inferred.length === 0) return (configQuery.data?.default_surfaces || []).filter((slug) => surfaces.some((surface) => surface.slug === slug && surface.is_enabled && surface.prepacks_enabled !== true))
    return [...new Set(inferred)]
  }, [configQuery.data?.default_surfaces, requestedSurface, selectedLegs, surfaces])

  const selectedSurfaces = surfaceOverride ?? inferredSurfaces
  const previewSurface = requestedSurface || (selectedSurfaces.length === 1 ? surfaces.find((surface) => surface.slug === selectedSurfaces[0]) : null)

  // Derived structural bet type. deriveBetType, like the public bet_type, leaves one leg untyped; a single
  // selection with no recipe saves as a straight, so it is named one here.
  const computedBetType = useMemo(() => deriveBetType(selectedLegs), [selectedLegs])
  const structureLabel = selectedLegs.length === 1 && !editQuery.data?.recipe_id ? 'Straight' : betTypeLabel(computedBetType)
  const canonicalLegs = useMemo(() => selectedLegs.map(canonicalLeg), [selectedLegs])
  const saveScopeIdentity = useMemo(
    () => JSON.stringify({
      org: slug || '',
      editId: editId || '',
      source: effectiveSourceRegulation,
      targets: [...targetRegulations].sort(),
    }),
    [editId, effectiveSourceRegulation, slug, targetRegulations],
  )

  useEffect(() => {
    saveScopeRef.current = saveScopeIdentity
    saveGenerationRef.current += 1
  }, [saveScopeIdentity])

  const minLegs = publishMinLegs(editQuery.data)
  const quote = usePrepackQuoteTargets({
    slug,
    canConfigure,
    sourceRegulation: effectiveSourceRegulation,
    targetRegulations,
    previewRegulation,
    selectedLegs,
    canonicalLegs,
    minLegs,
  })
  const {
    gateTargetMap,
    previewTarget,
    pendingPreviewLegs,
    hasRejectedTargets,
    showRejectedCombination,
    hasUnresolved,
    draftReady,
    quoteReady,
  } = quote
  // The first leg as its card row reads it (#865): "Paolo Banchero 25+ Points
  // Straight". A moneyline is its team alone ("Manchester United Straight"),
  // and a leg without a participant keeps its selection alone.
  const titleLeg = selectedLegs[0]
  const titleBet = titleLeg?.participant && String(titleLeg.market_family || '').toLowerCase() !== 'team_moneyline' ? legBetLabel(titleLeg) : ''
  const defaultTitle = titleLeg ? [String(titleLeg.participant || titleLeg.selection_name || '').trim(), titleBet, structureLabel || 'Parlay'].filter(Boolean).join(' ') : 'Untitled prepack'
  // Without title and tagline: the Live* components add them.
  const previewPack = useMemo(() => {
    if (!targetHasCanonicalPack(previewTarget)) return null
    const targetPack = previewTarget.pack
    const pending = pendingPreviewLegs
    const unpriced = Boolean(pending && !pending.sameLegs)
    return {
      id: editId || 'preview',
      bet_type: unpriced ? (computedBetType || '') : (targetPack.bet_type ?? ''),
      odds: unpriced ? null : (targetPack.odds ?? null),
      payout_multiplier: unpriced ? null : (targetPack.payout_multiplier ?? null),
      evaluation: unpriced ? undefined : targetPack.evaluation,
      legs: withPreviewTeamChoices(withLegChoices(pending ? pending.legs : targetPack.legs, selectedLegs)),
      regulation: targetPack.regulation || previewTarget.regulation,
      template_id: templateId ?? null,
      template_vars: templateVars || {},
      tags,
    }
  }, [computedBetType, editId, pendingPreviewLegs, previewTarget, selectedLegs, tags, templateId, templateVars])
  const fallbackAppearancePack = useMemo(() => ({ template_id: templateId ?? null, template_vars: templateVars || {} }), [templateId, templateVars])
  const selectedTemplate = useMemo(() => templates.find((template) => template.id === templateId) || null, [templates, templateId])
  // The legs that show a jersey (a team leg does when it has no logo or logos
  // are off) or can carry a shirt number, with the team the check resolved for
  // each, for the Jerseys box. Before a leg has been checked its search team
  // stands in; once checked, the check's answer wins even when it is "no
  // team", because that is what a save applies. The preview's choices are applied by the box itself.
  // A template that hides shirt numbers (#602) asks for none; otherwise player legs can carry the operator's shirt number.
  const jerseyLegs = useMemo(() => {
    if (!templateShowsJerseys(selectedTemplate)) return null
    const numbers = templateShowsJerseyNumbers(selectedTemplate)
    const quoted = targetHasCanonicalPack(previewTarget) ? (pendingPreviewLegs?.legs ?? previewTarget.pack.legs) : []
    return selectedLegs.flatMap((leg, index) => {
      const checked = counterpartLeg(quoted, leg, index, selectedLegs.length)
      const team = checked ? checked.team : leg.team
      const player = numbers && (isPlayerLeg(leg) || isPlayerLeg(checked))
      // A number typed for another template stays on the leg but is not drawn on this one's thumbnail.
      return player || team ? [{ ...leg, team, player, ...(numbers ? {} : { player_number: '' }) }] : []
    })
  }, [pendingPreviewLegs, previewTarget, selectedLegs, selectedTemplate])
  const setLegChoice = (outcomeId, key, value) => setSelectedLegs((current) => current.map((leg) => (
    leg.outcome_id === outcomeId ? { ...leg, [key]: value } : leg
  )))

  const toggleSurface = (surfaceSlug) => {
    setSurfaceOverride((current) => {
      const selected = current ?? inferredSurfaces
      return selected.includes(surfaceSlug) ? selected.filter((value) => value !== surfaceSlug) : [...selected, surfaceSlug]
    })
  }

  // Picking a selected outcome again in search removes it; anything else goes
  // through addLegs' selection rules.
  const addLeg = (leg) => {
    if (selectedLegs.some((item) => item.outcome_id === leg.outcome_id)) {
      removeLeg(leg.outcome_id)
      return
    }
    addLegs([leg])
  }

  // Adds legs (never toggles) against the growing selection, enforcing the
  // leg cap and one outcome per market, and reports each problem once.
  const addLegs = (legs) => {
    const next = [...selectedLegs]
    let reachedMax = false
    let sameMarket = false
    for (const leg of legs) {
      if (next.some((item) => item.outcome_id === leg.outcome_id)) continue
      if (next.length >= maxLegs) {
        reachedMax = true
        break
      }
      if (next.some((item) => item.market_id && item.event_id === leg.event_id && item.market_id === leg.market_id)) {
        sameMarket = true
        continue
      }
      next.push(leg)
    }
    if (reachedMax) toast.error('Maximum leg count reached')
    if (sameMarket) toast.error('Choose only one outcome from each market')
    if (next.length === selectedLegs.length) return
    setSourceRegulation(effectiveSourceRegulation)
    setSelectedLegs(next)
  }

  const removeLeg = (outcomeId) => setSelectedLegs((current) => current.filter((leg) => leg.outcome_id !== outcomeId))
  const moveLeg = (outcomeId, direction) =>
    setSelectedLegs((current) => {
      const from = current.findIndex((leg) => leg.outcome_id === outcomeId)
      const to = from + direction
      if (from < 0 || to < 0 || to >= current.length) return current
      const next = [...current]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })


  const save = async (publish) => {
    if (bannerPainting) return
    if (selectedLegs.length < (publish ? minLegs : 1)) {
      toast.error(publish ? `Add at least ${minLegs === 1 ? 'one leg' : `${minLegs} legs`} to publish` : 'Add at least one leg to save a draft')
      return
    }
    if (targetRegulations.length === 0) {
      toast.error('Select at least one regulation')
      return
    }
    if (selectedSurfaces.length === 0) {
      toast.error('Select at least one surface')
      return
    }
    const copy = copyStore.get()
    const resolvedCopy = resolvePackCopy(copy, defaultTitle)

    const ready = publish ? quoteReady : draftReady
    if (!ready) {
      toast.error(
        publish
          ? 'Wait for every target regulation to be priced successfully.'
          : 'Wait for every target regulation to be resolved.',
      )
      return
    }

    // Snapshot the operation scope. A keyed builder unmount or an org/target
    // scope change must prevent late responses from mutating the new builder.
    const saveSlug = slug
    const saveScope = saveScopeIdentity
    const saveGeneration = saveGenerationRef.current
    const isSaveActive = () => (
      mountedRef.current
      && saveGenerationRef.current === saveGeneration
      && saveScopeRef.current === saveScope
    )
    const saveTargets = [...targetRegulations]

    if (!isSaveActive()) return

    // Allocate all target IDs before the first network request. These IDs are
    // operation-scoped and survive a lost response or a partial retry.
    for (const regulation of saveTargets) {
      if (!targetDraftsRef.current[regulation]) {
        targetDraftsRef.current[regulation] = { id: targetDraftId(), persisted: false, newPack: true }
      }
    }

    setSaving(true)
    const savedPacks = []
    const errors = []
    // Titles of the shelf the saved title resembles (#984): saved anyway, shown once.
    const titleWarnings = new Set()
    // The saved title only names the bet (#1110): saved anyway, said once.
    let marketTitle = false
    const nextSaveStates = { ...targetSaveStates }

    try {
      for (const reg of saveTargets) {
        // Check before every target and after every awaited mutation. Already
        // dispatched server mutations are not cancellable, but their late
        // responses must not affect a different builder scope.
        if (!isSaveActive()) break

        const targetResult = gateTargetMap.get(reg)
        const targetPack = targetResult?.pack
        const existingDraft = targetDraftsRef.current[reg]
        const targetLegs = targetPack?.legs

        if (!targetHasCanonicalPack(targetResult)) {
          const message = targetResult?.error?.message || 'The target has no canonical wager to save.'
          errors.push(`${regulationLabel(reg, availableRegulations)}: ${message}`)
          nextSaveStates[reg] = {
            ...(nextSaveStates[reg] || {}),
            contentFingerprint: null,
            wagerFingerprint: null,
            saved: false,
            published: false,
            error: message,
          }
          continue
        }

        const existingMetadata = {}
        for (const key of ['recipe_id', 'recipe_revision', 'pack_type']) {
          if (existingDraft[key] != null) existingMetadata[key] = existingDraft[key]
        }
        const packPayloadData = {
          title: resolvedCopy.title,
          title_source: copy.titleSource,
          // "" clears a tagline; an omitted one is kept by the API.
          subtitle: resolvedCopy.subtitle,
          subtitle_source: copy.subtitleSource,
          sport: targetLegs[0]?.sport || 'general',
          league: targetLegs[0]?.league || null,
          odds: targetPack.odds ?? null,
          payout_multiplier: targetPack.payout_multiplier ?? null,
          bet_type: targetPack.bet_type ?? '',
          template_id: templateId ?? null,
          // A pack this page creates (another regulation added to an edited pack included) has its picture
          // settled here, so it is marked static on every save, after the edited pack's own mark: an older API
          // pod, which neither stamps nor keeps the mark, then never leaves it to the banner worker.
          template_vars: existingDraft.newPack
            ? { ...(templateVars || {}), [BANNER_MODE_VAR]: BANNER_MODE_STATIC }
            : templateVars || {},
          tags: tags || [],
          regulation: reg,
          legs: withLegChoices(targetLegs, selectedLegs),
          surfaces: selectedSurfaces,
          ...(targetPack.evaluation ? { evaluation: targetPack.evaluation } : {}),
          ...existingMetadata,
        }
        const contentFingerprint = targetContentFingerprint(reg, packPayloadData)
        const wagerFingerprint = targetWagerFingerprint(reg, targetPack)
        const previousState = nextSaveStates[reg]
        const sameReviewedTarget = (
          previousState?.contentFingerprint === contentFingerprint
          && previousState?.wagerFingerprint === wagerFingerprint
        )
        let saved = sameReviewedTarget && previousState.saved ? previousState.pack : null
        let published = sameReviewedTarget && previousState.published
        let finalPack = saved

        try {
          if (!saved) {
            const updatePayload = {
              ...packPayloadData,
              ...(versionToken(existingDraft.version) != null ? { updated_at: versionToken(existingDraft.version) } : {}),
            }
            saved = existingDraft.persisted
              ? await prepacksApi.updatePrepack(saveSlug, existingDraft.id, updatePayload)
              : await prepacksApi.createPrepack(saveSlug, { ...updatePayload, id: existingDraft.id, status: 'draft' })
            if (!isSaveActive()) break
            for (const warning of saved?.title_warnings || []) {
              if (warning?.reason === 'market_title') marketTitle = true
              else if (warning?.title) titleWarnings.add(warning.title)
            }
            existingDraft.persisted = true
          }

          if (!isSaveActive()) break
          const savedVersion = versionToken(saved?.updated_at ?? saved?.version) ?? versionToken(existingDraft.version)
          targetDraftsRef.current[reg] = {
            ...existingDraft,
            id: saved?.id || existingDraft.id,
            version: savedVersion,
            status: saved?.status ?? existingDraft.status,
            persisted: true,
            recipe_id: saved?.recipe_id ?? existingDraft.recipe_id,
            recipe_revision: saved?.recipe_revision ?? existingDraft.recipe_revision,
            pack_type: saved?.pack_type ?? existingDraft.pack_type,
          }
          if (saved?.status === 'published') published = true

          nextSaveStates[reg] = {
            ...(previousState || {}),
            contentFingerprint,
            wagerFingerprint,
            saved: true,
            published,
            pack: saved,
            version: savedVersion,
            error: null,
          }
          setTargetSaveStates({ ...nextSaveStates })

          if (publish && !published) {
            if (!isSaveActive()) break
            finalPack = await prepacksApi.publishPrepack(saveSlug, saved?.id || existingDraft.id)
            if (!isSaveActive()) break
            published = true
            const publishedVersion = versionToken(finalPack?.updated_at ?? finalPack?.version) ?? savedVersion
            targetDraftsRef.current[reg] = {
              ...targetDraftsRef.current[reg],
              status: finalPack?.status || 'published',
              version: publishedVersion,
            }
            nextSaveStates[reg] = {
              ...nextSaveStates[reg],
              published: true,
              pack: finalPack || saved,
              version: publishedVersion,
              error: null,
            }
            setTargetSaveStates({ ...nextSaveStates })
          }

          if (!isSaveActive()) break
          savedPacks.push(finalPack || saved)
        } catch (error) {
          if (!isSaveActive()) break
          const message = errorMessage(error)
          errors.push(`${regulationLabel(reg, availableRegulations)}: ${message}`)
          // A failed publish leaves a durable draft. Retrying must publish it
          // rather than POSTing the draft again.
          nextSaveStates[reg] = {
            ...(nextSaveStates[reg] || previousState || {}),
            contentFingerprint,
            wagerFingerprint,
            saved: Boolean(saved),
            published: Boolean(saved && published),
            pack: saved || previousState?.pack,
            error: message,
          }
          setTargetSaveStates({ ...nextSaveStates })
          if (saved) savedPacks.push(saved)
        }
      }

      if (!isSaveActive()) return
      if (savedPacks.length > 0) {
        // Not awaited: the result is built from savedPacks, and waiting held it
        // behind every active prepacks read, the sportsbook re-quote among them.
        queryClient.invalidateQueries({ queryKey: ['prepacks'] })
      }

      if (errors.length === 0 && savedPacks.length === saveTargets.length) {
        const primaryPack = savedPacks[0]
        const resultStatus = primaryPack?.status || (publish ? 'published' : 'draft')
        const resultIsPublished = publish || resultStatus === 'published'
        // A stamped draft's result names the schedule it is listed under
        // (#1080), so it waits for that one read: the schedule list refetch
        // the invalidation started, joined rather than sent again.
        if (!resultIsPublished && resultStatus !== 'scheduled' && packRecipeId(primaryPack)) {
          await queryClient.refetchQueries({ queryKey: scheduleQueryKeys.list(saveSlug), type: 'active' }, { cancelRefetch: false })
          if (!isSaveActive()) return
        }
        // An update or a publish answers with the players, teams and market
        // shapes the pack now puts past a repeat limit: shown, never refused.
        const repeatWarnings = repeatWarningLines(savedPacks, surfaceNamer(surfacesQuery.data?.surfaces))
        setResult({ pack: primaryPack, packs: savedPacks, published: resultIsPublished, status: resultStatus, count: savedPacks.length, repeatWarnings })
        toast.success(
          resultIsPublished
            ? `Prepack published (${savedPacks.length} regulation${savedPacks.length > 1 ? 's' : ''})`
            : resultStatus === 'scheduled'
            ? `Prepack schedule updated (${savedPacks.length} regulation${savedPacks.length > 1 ? 's' : ''})`
            : `Draft saved (${savedPacks.length} regulation${savedPacks.length > 1 ? 's' : ''})`,
        )
        if (titleWarnings.size > 0) {
          toast.warning('This title is close to another pack’s', { description: `Similar to ${Array.from(titleWarnings, (title) => `“${title}”`).join(', ')}.` })
        }
        if (marketTitle) {
          toast.warning('This title only names the bet', { description: 'The card already shows the market and the line. A title with a hook of its own reads better.' })
        }
      } else if (errors.length > 0) {
        toast.error(`Some regulations failed: ${errors.join('; ')}`)
      }
    } finally {
      if (isSaveActive()) setSaving(false)
    }
  }

  if (editId && editQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-[var(--prepacks-bg)] p-8">
        <StatePanel state="loading" title="Loading prepack" description="Retrieving the saved builder state." />
      </div>
    )
  }
  if (editId && editQuery.isError) {
    return (
      <div className="flex flex-1 items-center justify-center bg-[var(--prepacks-bg)] p-8">
        <StatePanel
          state="error"
          title="Prepack could not be loaded"
          description={editQuery.error?.response?.data?.error || 'Try opening the builder again.'}
          onRetry={() => editQuery.refetch()}
        />
      </div>
    )
  }

  if (result) {
    const resultPack = result.pack
    // Where a draft is listed (#1080): its live schedule's Packs, else Schedules → Unscheduled packs.
    // A stamped draft waits for the schedule lookup to answer (a name, or
    // gone), so it never links to a schedule deleted since the list was read.
    const isDraftResult = !result.published && result.status !== 'scheduled'
    const resultSource = resultRecipeId ? scheduleSourceOf(resultPack) : null
    const listedOnSchedule = Boolean(resultSource?.name && !resultSource.missing)
    const listedUnscheduled = !resultRecipeId || Boolean(resultSource?.missing)
    // Library is out of the sidebar (#1056): reopen a saved draft instead. The reset
    // also leaves this screen when the draft was already open at the same URL, and
    // replace keeps the transient result screen out of history.
    const reopenDraft = (id) => {
      onReset()
      navigate(`/dashboard/prepacks/generate?id=${encodeURIComponent(id)}`, { replace: true })
    }
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 bg-[var(--prepacks-bg)] px-8 text-center text-[var(--wf-color-text)]">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--wf-color-success-soft)]">
          <Check className="h-7 w-7 text-[var(--wf-color-success)]" />
        </div>
        <h2 className="text-xl font-semibold">
          {result.published ? 'Prepack published' : result.status === 'scheduled' ? 'Prepack schedule updated' : 'Draft saved'}
        </h2>
        <p className="text-sm text-[var(--prepacks-muted)]">
          “{resultPack.title}” is{' '}
          {result.published
            ? `live on its selected surfaces across ${result.count || 1} regulation${(result.count || 1) > 1 ? 's' : ''}`
            : result.status === 'scheduled'
            ? 'scheduled on its selected surfaces'
            : result.count > 1
            ? `saved as a draft in ${result.count} regulations`
            : 'saved as a draft'}
          .
        </p>
        {isDraftResult && (listedOnSchedule || listedUnscheduled) ? (
          <p className="text-xs text-[var(--prepacks-muted)]">
            Listed under{' '}
            <Link
              to={listedOnSchedule ? schedulePacksPath(resultSource.id) : UNSCHEDULED_PACKS_PATH}
              className="font-semibold text-[var(--prepacks-accent-text)] underline-offset-2 hover:underline"
            >
              {listedOnSchedule ? `Packs on “${resultSource.name}”` : 'Schedules → Unscheduled packs'}
            </Link>
            .
          </p>
        ) : null}
        {!result.published && result.packs?.length > 1 ? (
          <div role="group" aria-label="Drafts by regulation" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-[var(--prepacks-muted)]">
            <span>Edit a regulation’s draft:</span>
            {result.packs.map((pack) => (
              <button
                key={pack.id}
                type="button"
                onClick={() => reopenDraft(pack.id)}
                className="font-semibold text-[var(--prepacks-accent-text)] underline-offset-2 hover:underline"
              >
                {regulationLabel(pack.regulation, availableRegulations)}
              </button>
            ))}
          </div>
        ) : null}
        {result.repeatWarnings?.length ? (
          <section aria-label="Repeat warnings" className="w-full max-w-md rounded-lg border border-[var(--wf-color-warning-border)] bg-[var(--wf-color-warning-soft)] px-3 py-2.5 text-left text-xs text-[var(--wf-color-warning)]">
            <p className="flex items-center gap-1.5 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Past the repeat limits
            </p>
            <ul className="mt-1 flex flex-col gap-0.5 pl-5">
              {result.repeatWarnings.map((line) => <li key={line}>{line}</li>)}
            </ul>
          </section>
        ) : null}
        <PrepackSurfacePreviewProvider surface={previewSurface}>
          <div className="mt-2 w-72 text-left">
            <PrepackCard pack={resultPack} showActions={false} templates={templates} />
          </div>
        </PrepackSurfacePreviewProvider>
        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={() => {
              onReset()
              navigate('/dashboard/prepacks/generate')
            }}
            className="rounded border border-[var(--prepacks-border)] px-4 py-2 text-xs font-semibold text-[var(--wf-color-text)] transition-colors hover:bg-[var(--prepacks-raised)]"
          >
            Build another
          </button>
          <button
            type="button"
            onClick={() => {
              if (result.published) navigate('/dashboard/prepacks/surfaces')
              else reopenDraft(resultPack.id)
            }}
            className="rounded bg-[var(--prepacks-accent)] px-4 py-2 text-xs font-semibold text-[var(--wf-color-on-action)] transition-colors hover:bg-[var(--prepacks-accent-hover)]"
          >
            {result.published ? 'View on surfaces' : 'Keep editing'}
          </button>
        </div>
      </div>
    )
  }
  const writable = canConfigure && (editQuery.data?.status !== 'published' || canPublish)
  const controlsDisabled = !writable || saving

  const isPublishBlocked =
    controlsDisabled ||
    !configQuery.data ||
    !canPublish ||
    selectedLegs.length < minLegs ||
    selectedSurfaces.length === 0 ||
    targetRegulations.length === 0 ||
    !quoteReady ||
    hasRejectedTargets ||
    hasUnresolved ||
    bannerPainting
  // relative: sr-only headings/legends are absolutely positioned; without a positioned ancestor they anchor to the viewport and stretch the document past the shell (#1066).
  return (
    <PrepackSurfacePreviewProvider surface={previewSurface}>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto bg-[var(--prepacks-bg)] text-[var(--wf-color-text)] lg:flex-row lg:overflow-hidden">
        <section className="flex min-w-0 flex-1 flex-col border-b border-[var(--prepacks-border)] lg:overflow-hidden lg:border-b-0 lg:border-r">
          <div className="flex-1 px-5 pb-6 pt-6 sm:px-8 lg:overflow-y-auto">
            {configQuery.isError ? (
              <StatePanel state="error" title="Configuration could not be loaded" onRetry={() => configQuery.refetch()} />
            ) : null}
            <div className="mb-6">
              <p className="text-sm font-semibold">{editId ? 'Edit prepack' : 'Build a prepack'}</p>
              {editSource ? (
                <p className="mt-1 text-xs text-[var(--prepacks-muted)]">
                  {editSource.missing ? (
                    'Generated by a schedule that has since been deleted.'
                  ) : (
                    <>
                      Generated by{' '}
                      <Link
                        to={schedulePath(editSource.id)}
                        onClick={leaveForSchedule}
                        onMouseEnter={preloadScheduleBuilder}
                        onFocus={preloadScheduleBuilder}
                        className="text-[var(--prepacks-accent-text)] underline underline-offset-2"
                      >
                        {editSource.name || 'its schedule'}
                      </Link>
                      . What you change here stays on this pack.
                    </>
                  )}
                </p>
              ) : null}
              {editedFields.length > 0 ? (
                <p className="mt-1 text-xs text-[var(--prepacks-muted)]">{`Edited on this pack: ${editedFields.map((item) => item.label).join(', ')}.`}</p>
              ) : null}
            </div>

            {/* G1: Template selection */}
            <div className="mb-7">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--prepacks-muted)]">1. Card template</span>
                <button
                  type="button"
                  disabled={controlsDisabled}
                  onClick={() => navigate('/dashboard/prepacks/configuration')}
                  className="text-[11px] text-[var(--prepacks-accent-text)] hover:underline disabled:pointer-events-none disabled:opacity-40"
                >
                  Create template →
                </button>
              </div>
              {templatePending ? (
                <p role="status" className="text-xs text-[var(--prepacks-muted)]">Loading templates…</p>
              ) : (
                <LiveTemplatePicker
                  store={copyStore}
                  defaultTitle={defaultTitle}
                  templates={templates}
                  value={templateId}
                  onChange={(newId) => {
                    setTemplateId(newId)
                    // The new template starts from its own values, but the pack keeps TEMPLATE_SWITCH_KEPT_VARS.
                    setTemplateVars(templateSwitchVars)
                  }}
                  pack={previewPack}
                  disabled={controlsDisabled}
                />
              )}
            </div>

            {/* G2 & G3: Add bets, target regulations, early rejection */}
            <div className="mb-7">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-[var(--prepacks-muted)]">2. Add bets</span>

              {availableRegulations.length > 1 ? (
                <label className="mb-3 block text-xs text-[var(--prepacks-muted)]">
                  Search bets in
                  <select
                    aria-label="Search bets in"
                    title={selectedLegs.length > 0
                      ? 'Remove all bets to change where bets are searched.'
                      : 'Bets are found and priced in this regulation, then re-priced for every regulation you publish to.'}
                    className="ml-3 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-2 text-[var(--wf-color-text)]"
                    value={effectiveSourceRegulation}
                    disabled={selectedLegs.length > 0 || controlsDisabled}
                    onChange={(event) => {
                      setSourceRegulation(event.target.value)
                      setTargetRegulations(initialTargetsFor(event.target.value))
                      setPreviewRegulation(event.target.value)
                    }}
                  >
                    {availableRegulations.map((item) => (
                      <option key={item.offering_id} value={item.offering_id}>
                        {regulationLabel(item.offering_id, availableRegulations)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {availableRegulations.length > 0 ? (
                <div className="mb-3">
                  <PrepackRegulationPicker
                    regulations={availableRegulations}
                    value={targetRegulations}
                    onChange={setTargetRegulations}
                    disabled={controlsDisabled}
                    label="Target regulations"
                  />
                </div>
              ) : null}

              <LegSearch
                context="prepacks"
                orgSlug={slug}
                role={currentOrg?.role}
                regulationMappings={availableRegulations}
                offerings={[effectiveSourceRegulation]}
                query={legSearch}
                onQueryChange={setLegSearch}
                selectedIds={selectedLegs.map((leg) => leg.outcome_id)}
                onSelect={(searchResult) => addLeg(searchResult.leg)}
                enabled={canSearchOutcomes && writable && !saving}
                help="Search by player, team, game, market, or a market or outcome ID. Add 2 or more bets to quote and verify live sportsbook odds."
                placeholder="Search player, team, game, or a market/outcome ID"
                emptyMessage="No available bets match this search."
              />

              {/* Selected legs list */}
              {selectedLegs.length > 0 ? (
                <div className="mb-3 mt-3 flex flex-col gap-1.5">
                  <p
                    ref={selectedLegsHeadingRef}
                    tabIndex={-1}
                    className="text-[10px] font-semibold uppercase tracking-widest text-[var(--prepacks-muted)] focus:outline-none"
                  >
                    Selected legs ({selectedLegs.length})
                  </p>
                  <LegList
                    legs={selectedLegs}
                    label="Selected legs"
                    onRemove={controlsDisabled ? undefined : (leg) => removeLeg(leg.outcome_id)}
                    onMove={controlsDisabled ? undefined : (leg, direction) => moveLeg(leg.outcome_id, direction)}
                  />
                </div>
              ) : null}

              <PrepackSuggestedLegs
                orgSlug={slug}
                regulation={effectiveSourceRegulation}
                legs={canonicalLegs}
                displayLegs={selectedLegs}
                enabled={canSearchOutcomes && writable && !saving}
                maxLegs={maxLegs}
                rejected={showRejectedCombination}
                onAdd={(leg) => addLegs([leg])}
                onAddAll={addLegs}
                focusFallbackRef={selectedLegsHeadingRef}
              />

              {regulationQuery.isError ? (
                <p className="rounded border border-[var(--wf-color-danger-border)] bg-[var(--wf-color-danger-soft)] p-3 text-xs text-[var(--wf-color-danger)]">
                  {regulationQuery.error?.response?.data?.error || 'Regulations could not be loaded.'}
                </p>
              ) : null}

              {/* G3: Prominent early rejection & quote status immediately beneath legs */}
              {selectedLegs.length > 0 ? (
                <PrepackQuoteStatus
                  quote={quote}
                  legCount={selectedLegs.length}
                  targetRegulations={targetRegulations}
                  availableRegulations={availableRegulations}
                  controlsDisabled={controlsDisabled}
                />
              ) : null}
              {targetRegulations.some((regulation) => targetSaveStates[regulation]?.error) ? (
                <div role="alert" className="mt-2 rounded border border-[var(--wf-color-danger-border)] bg-[var(--wf-color-danger-soft)] p-3 text-xs text-[var(--wf-color-danger)]">
                  <p className="font-semibold">Some regulation drafts need attention</p>
                  <ul className="mt-1 list-disc pl-4">
                    {targetRegulations.map((regulation) =>
                      targetSaveStates[regulation]?.error ? (
                        <li key={regulation}>{regulationLabel(regulation, availableRegulations)}: {targetSaveStates[regulation].error}</li>
                      ) : null,
                    )}
                  </ul>
                </div>
              ) : null}
            </div>

            {/* G4 & G5: Name, computed structure, custom tags */}
            <div className="mb-7">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-[var(--prepacks-muted)]">3. Title, tagline & tags</span>
              <div className="flex flex-col gap-3">
                <LiveCopyFields
                  store={copyStore}
                  orgSlug={slug}
                  regulation={effectiveSourceRegulation}
                  legs={canonicalLegs}
                  disabled={controlsDisabled}
                  aiStatus={titleAiStatus}
                  aiUnavailableReason={prepackTitleFeature?.reason?.message || ''}
                  autoSuggest
                  skipLegKey={loadedLegKey}
                />

                <div>
                  <span className="mb-1 block text-xs text-[var(--prepacks-muted)]">Betting structure</span>
                  <div className="flex items-center gap-2">
                    <span className="inline-block rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-2.5 py-1 text-xs font-semibold text-[var(--wf-color-text)]">
                      {selectedLegs.length < 2 && !structureLabel ? 'Add bets to determine type' : structureLabel}
                    </span>
                  </div>
                </div>

                <PrepackTagsField
                  value={tags}
                  onChange={(next) => {
                    setTags(next)
                    // Filled does not outlive the last tag (the API drops it too).
                    if (next.length === 0 && templateVars?.[TAG_STYLE_VARIABLE]) setTemplateVars((current) => withTagStyle(current, TAG_STYLE_QUIET))
                  }}
                  tagStyle={templateVars?.[TAG_STYLE_VARIABLE] === TAG_STYLE_FILLED ? TAG_STYLE_FILLED : TAG_STYLE_QUIET}
                  onTagStyleChange={(style) => setTemplateVars((current) => withTagStyle(current, style))}
                  disabled={controlsDisabled}
                />
              </div>
            </div>

            {/* Surfaces */}
            <div className="mb-4">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-[var(--prepacks-muted)]">4. Surfaces</span>
              {surfacesQuery.isLoading ? (
                <p className="text-xs text-[var(--prepacks-muted)]">Loading surfaces…</p>
              ) : surfacesQuery.isError ? (
                <p className="text-xs text-[var(--wf-color-danger)]">Surfaces could not be loaded.</p>
              ) : (
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {surfaces
                    .filter((surface) => surface.is_enabled)
                    .map((surface) => {
                      const selected = selectedSurfaces.includes(surface.slug)
                      return (
                        <button
                          key={surface.slug}
                          type="button"
                          disabled={controlsDisabled}
                          aria-pressed={selected}
                          onClick={() => toggleSurface(surface.slug)}
                          className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            selected
                              ? 'border-[var(--prepacks-accent-text)]/40 bg-[var(--wf-color-action-soft)]'
                              : 'border-[var(--prepacks-border)] hover:border-[var(--prepacks-muted)]'
                          }`}
                        >
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] ${
                              selected
                                ? 'border-[var(--prepacks-accent-text)] bg-[var(--prepacks-accent)] text-[var(--wf-color-on-action)]'
                                : 'border-[var(--prepacks-border)]'
                            }`}
                          >
                            {selected ? <Check className="h-3 w-3" /> : null}
                          </span>
                          <span className="truncate text-xs font-medium text-[var(--wf-color-text)]">{surfaceLabel(surface)}</span>
                          {' '}<PrepackSurfaceLiveTag surface={surface} />
                        </button>
                      )
                    })}
                </div>
              )}
            </div>
          </div>

          <footer className="sticky bottom-0 flex shrink-0 items-center justify-between gap-3 border-t border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-5 py-3.5 sm:px-8 lg:static">
            <button
              type="button"
              onClick={() => save(false)}
              disabled={!writable || !configQuery.data || selectedLegs.length === 0 || targetRegulations.length === 0 || saving || bannerPainting}
              aria-describedby={bannerPainting ? paintingNoteId : undefined}
              className="rounded border border-[var(--prepacks-border)] px-4 py-2 text-xs font-semibold text-[var(--wf-color-text)] transition-colors hover:bg-[var(--prepacks-raised)] disabled:opacity-40"
            >
              {saving ? 'Saving…' : editId ? 'Save changes' : 'Save as draft'}
            </button>
            <div className="text-right">
              <button
                type="button"
                onClick={() => save(true)}
                disabled={isPublishBlocked}
                aria-describedby={bannerPainting ? paintingNoteId : undefined}
                className="rounded bg-[var(--prepacks-accent)] px-4 py-2 text-xs font-semibold text-[var(--wf-color-on-action)] transition-colors hover:bg-[var(--prepacks-accent-hover)] disabled:opacity-40"
              >
                {saving
                  ? 'Publishing…'
                  : `Publish to ${selectedSurfaces.length} surface${selectedSurfaces.length === 1 ? '' : 's'}`}
              </button>
              {showRejectedCombination && (
                <p className="mt-1 text-[11px] text-[var(--wf-color-danger)]">Resolve rejected combination before publishing</p>
              )}
              {bannerPainting ? <p id={paintingNoteId} className="mt-1 text-[11px] text-[var(--prepacks-muted)]">Wait for the picture to finish.</p> : null}
            </div>
          </footer>
        </section>

        {/* G6: Simplified Live Preview — card and colors only, no duplicate side panels */}
        <PrepackLivePreview
          quote={quote}
          copyStore={copyStore}
          previewPack={previewPack}
          fallbackAppearancePack={fallbackAppearancePack}
          defaultTitle={defaultTitle}
          templates={templates}
          selectedTemplate={selectedTemplate}
          orgSlug={slug}
          targetRegulations={targetRegulations}
          previewRegulation={previewRegulation}
          onPreviewRegulationChange={setPreviewRegulation}
          availableRegulations={availableRegulations}
          controlsDisabled={controlsDisabled || templatePending}
          onTemplateVarsChange={setTemplateVars}
          onPaintingChange={setBannerPainting}
          jerseyLegs={jerseyLegs}
          jerseyNumbersNeedResave={templateJerseyNumbersNeedResave(selectedTemplate)}
          onLegChoiceChange={setLegChoice}
        />
      </div>
    </PrepackSurfacePreviewProvider>
  )
}
